/* src/crypto/mediaMeta.js — a photo's caption and blurred preview, sealed like the photo.
   Twin of the phone's services/mediaMeta.ts — the same format both ways (see the note there):
   { c: caption, t: photo thumbnail, v: video thumbnail, b: album thumbnails } boxed with
   nacl.secretbox under the media key, carried as `metaCipher` + `metaNonce`. */
import nacl from 'tweetnacl';
import util from 'tweetnacl-util';

export const PLAIN_META_FIELDS = ['caption', 'originalCaption', 'imageThumbnailBase64', 'videoThumbnailBase64', 'batchThumbnails'];

const keyOf = (kB64) => {
  try { const k = util.decodeBase64(String(kB64 || '')); return k.length === nacl.secretbox.keyLength ? k : null; } catch { return null; }
};

export function sealMediaMeta(kB64, meta) {
  const k = keyOf(kB64);
  if (!k) return null;
  const clean = {};
  if (typeof meta?.c === 'string' && meta.c) clean.c = meta.c;
  if (typeof meta?.t === 'string' && meta.t) clean.t = meta.t;
  if (typeof meta?.v === 'string' && meta.v) clean.v = meta.v;
  if (Array.isArray(meta?.b) && meta.b.length) clean.b = meta.b.map((x) => String(x || ''));
  const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);
  const box = nacl.secretbox(util.decodeUTF8(JSON.stringify(clean)), nonce, k);
  return { metaCipher: util.encodeBase64(box), metaNonce: util.encodeBase64(nonce) };
}

export function openMediaMeta(kB64, metaCipher, metaNonce) {
  const k = keyOf(kB64);
  if (!k || typeof metaCipher !== 'string' || typeof metaNonce !== 'string') return null;
  try {
    const out = nacl.secretbox.open(util.decodeBase64(metaCipher), util.decodeBase64(metaNonce), k);
    if (!out) return null;
    const m = JSON.parse(util.encodeUTF8(out));
    return m && typeof m === 'object' ? m : null;
  } catch { return null; }
}

export function withSealedMeta(payload, sealed) {
  if (!payload || !sealed) return payload;
  const out = { ...payload };
  for (const f of PLAIN_META_FIELDS) delete out[f];
  out.metaCipher = sealed.metaCipher;
  out.metaNonce = sealed.metaNonce;
  return out;
}

export function applyMediaMeta(row, m) {
  if (!row || !m) return false;
  let changed = !row.metaOpen;
  row.metaOpen = true;
  if (typeof m.c === 'string' && !(typeof row.caption === 'string' && row.caption)) { row.caption = m.c; changed = true; }
  if (typeof m.c === 'string' && m.c && row.forwarded && !row.originalCaption) { row.originalCaption = m.c; changed = true; }
  if (typeof m.t === 'string' && m.t && !row.imageThumbnailBase64) { row.imageThumbnailBase64 = m.t; changed = true; }
  if (typeof m.v === 'string' && m.v && !row.videoThumbnailBase64) { row.videoThumbnailBase64 = m.v; changed = true; }
  if (Array.isArray(m.b) && m.b.length && !(Array.isArray(row.batchThumbnails) && row.batchThumbnails.length)) { row.batchThumbnails = m.b; changed = true; }
  return changed;
}

export function metaPending(row) {
  if (!row || typeof row.metaCipher !== 'string' || typeof row.metaNonce !== 'string') return false;
  return row.metaOpen !== true;
}
